import {
  Alert,
  Badge,
  Card,
  Container,
  Grid,
  Group,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useEffect } from 'react';
import { useParams } from 'wouter';
import { CollapsibleCard } from '@/components/CollapsibleCard';
import { DeliverAction } from '@/components/client-portfolio/DeliverAction';
import { PortfolioList } from '@/components/client-portfolio/PortfolioList';
import Link from '@/components/Link';
import Loader from '@/components/Loader';
import { ApiError } from '@/lib/api';
import { useBreadcrumbs } from '@/lib/breadcrumbs';
import {
  apiErrorText,
  CLIENT_COMPANY_STATUS_COLORS,
  CLIENT_COMPANY_STATUS_LABELS,
  useContactPortfolio,
} from '@/models/clientCompanies';
import {
  CLIENT_COMPANIES_ALL,
  CLIENT_COMPANIES_DELIVER,
  CLIENT_COMPANIES_WRITE,
  useHasAnyPermission,
} from '@/models/permissions';

export default function ClientContactPortfolioPage() {
  const params = useParams<{ contactId: string }>();
  const contactId = Number(params.contactId);
  const { data, isLoading, error } = useContactPortfolio(contactId);
  const { setBreadcrumbs } = useBreadcrumbs();
  const canWriteRole = useHasAnyPermission([
    CLIENT_COMPANIES_WRITE,
    CLIENT_COMPANIES_ALL,
    '*:*',
  ]);
  const canDeliverRole = useHasAnyPermission([
    CLIENT_COMPANIES_DELIVER,
    CLIENT_COMPANIES_ALL,
    '*:*',
  ]);
  const canOpenContacts = useHasAnyPermission([
    'contacts:read',
    'contacts:read-all',
    'contacts:*',
    '*:*',
  ]);

  const name = data
    ? [data.contact.firstName, data.contact.lastName]
        .filter(Boolean)
        .join(' ') ||
      data.contact.email ||
      `#${data.contact.id}`
    : null;

  const readOnly = data?.readOnly === true;
  const canWrite = canWriteRole && !readOnly;
  const canDeliver = canDeliverRole && !readOnly;

  useEffect(() => {
    if (!name) return;
    setBreadcrumbs([
      {
        title: 'Client Companies',
        to: readOnly ? '/client-companies' : '/client-companies?tab=contacts',
      },
      { title: name, to: `/client-contacts/${contactId}` },
    ]);
    return () => setBreadcrumbs([]);
  }, [name, contactId, readOnly, setBreadcrumbs]);

  if (isLoading) return <Loader />;
  if (error || !data)
    return (
      <Paper p="md">
        <Alert color="gray">
          {error instanceof ApiError && error.status === 404
            ? 'This contact has no services in the portfolio.'
            : apiErrorText(error ?? 'Not found')}
        </Alert>
      </Paper>
    );

  const { contact, companies, services } = data;

  return (
    <Paper p="md" h="100%">
      <Container size="xl" px={0}>
        <Grid>
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Stack gap="md">
              <Card withBorder>
                <Stack gap={4}>
                  <Group gap="xs">
                    <Title order={3}>{name}</Title>
                    <Badge size="sm" variant="light" color="gray">
                      Services without a company
                    </Badge>
                  </Group>
                  <Group gap="lg">
                    {contact.email && (
                      <Text fz="sm" c="dimmed">
                        {contact.email}
                      </Text>
                    )}
                    {contact.phone && (
                      <Text fz="sm" c="dimmed">
                        {contact.phone}
                      </Text>
                    )}
                    <Text fz="sm" c="dimmed">
                      Account: {contact.accountName ?? '-'}
                    </Text>
                    {canOpenContacts && (
                      <Link to={`~/contacts/${contact.id}`}>Contact page</Link>
                    )}
                  </Group>
                  {!readOnly && (
                    <Text fz="xs" c="dimmed">
                      Company data is not required to mark these services
                      delivered.
                    </Text>
                  )}
                </Stack>
              </Card>
              <PortfolioList
                services={services}
                canWrite={canWrite}
                readOnly={readOnly}
                actions={
                  canDeliver
                    ? (service) => (
                        <DeliverAction
                          service={service}
                          owner={{ kind: 'contact' }}
                        />
                      )
                    : undefined
                }
              />
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <CollapsibleCard title="Client companies of the account">
              <Stack gap={4}>
                {companies.length === 0 && (
                  <Text fz="sm" c="dimmed">
                    None
                  </Text>
                )}
                {companies.map((c) => (
                  <Group key={c.id} justify="space-between" wrap="nowrap">
                    <Link to={`~/client-companies/${c.id}`}>{c.name}</Link>
                    <Badge
                      size="xs"
                      variant="light"
                      color={CLIENT_COMPANY_STATUS_COLORS[c.status]}
                    >
                      {CLIENT_COMPANY_STATUS_LABELS[c.status] ?? c.status}
                    </Badge>
                  </Group>
                ))}
              </Stack>
            </CollapsibleCard>
          </Grid.Col>
        </Grid>
      </Container>
    </Paper>
  );
}
